define(function(require, exports, module) {
    'use strict';

    const util = require('utils');
    const dateUtil = require('util/date.util');
    const BaseView = require('common/base.view');
    const BirthRegionModel = require('common/birthRegion.model');

    const ITALY_COUNTRY_CODE = 'IT';
    const DEFAULT_SOGEI_CODE = '99';

    module.exports = BaseView.extend({
        options: {
            dateCondition: 'today',
            countryCondition: ITALY_COUNTRY_CODE,
            defaultSogeiCode: DEFAULT_SOGEI_CODE,
            template: 'placeOfResidenceTmpl'
        },

        events: {
            'change [name="countryOfResidence"]': 'handleCountryOfResidenceChange',
            'change [name="provinceOfResidence"]': 'handleProvinceOfResidenceChange'
        },

        initialize: function(options) {
            this.options = { ...this.options, ...options };
            this.options.innerFields = Object.values(options.innerFields || {})
                .filter(field => field.active)
                .sort((field1, field2) => field1.order - field2.order)
                .map(field => {
                    field.isLabelAsteriskEnabled = field.validationRules?.some((rule) => rule.type === 'required' && rule.isLabelAsteriskEnabled);
                    field.isPlaceholderAsteriskEnabled = field.validationRules?.some((rule) => rule.type === 'required' && rule.isPlaceholderAsteriskEnabled);

                    return field;
                });

            this.options.dateCondition = new Date(dateUtil.parseStringDateToUnixFormat(this.options.dateCondition))
                .toJSON().slice(0, 10);

            this.model = new BirthRegionModel();

            this.initialFetch();
        },

        subscribe: function() {
            this.listenTo(this.model, 'change:birthCountries', this.updateCountriesList);
            this.listenTo(this.model, 'change:regionsByCountryCode', this.updateProvincesList);
        },

        render: function() {
            if (!this.options.innerFields.length) {
                return this;
            }

            this.$el.html(this.tmpl(this.options.template, this.options));
            this.$el.removeClass('fn-validate');
        },

        initialFetch: function() {
            this.toggleFieldVisibility('countryOfResidence', false);
            this.toggleFieldVisibility('provinceOfResidence', false);
            this.toggleFieldVisibility('cityOfResidence', false);

            this.model.fetchCountries(this.options.dateCondition)
                .done((birthCountries) => {
                    this.model.set({ birthCountries });
                    this.model.trigger('change:birthCountries');
                })
                .fail((errorMsg) => {
                    this.options.form.showErrorMessage(errorMsg);
                });
        },

        handleCountryOfResidenceChange: function(event) {
            this.toggleFieldVisibility('provinceOfResidence', false);
            this.toggleFieldVisibility('cityOfResidence', false);

            // countryOfResidence option value example: "Z114-GB"
            const countryCode = (event.target.value || '').split('-')[1];

            if (countryCode !== this.options.countryCondition) {
                this.$el.find('.fn-sogeiRegionCode').val(DEFAULT_SOGEI_CODE);

                return;
            }

            this.disableField('countryOfResidence');

            this.model.fetchRegionsByCountryCode(this.options.dateCondition, countryCode)
                .done((regionsByCountryCode) => {
                    this.model.set({ regionsByCountryCode });
                    this.model.trigger('change:regionsByCountryCode');
                })
                .fail((errorMsg) => {
                    this.options.form.showErrorMessage(errorMsg);
                })
                .always(() => {
                    this.enableField('countryOfResidence');
                });
        },

        handleProvinceOfResidenceChange: function(event) {
            const selectedProvinceCode = event.target.value;
            const sogeiRegionCode = this.model.getSogeiRegionCode(selectedProvinceCode);

            this.$el.find('.fn-sogeiRegionCode').val(sogeiRegionCode);

            this.updateCitiesList();
        },

        updateCountriesList: function() {
            const birthCountries = this.model.get('birthCountries');
            // regulationCode is used to validate italianID field
            // code is used to fetch regions and is sent in CreatePlayerInfoDataMap
            const options = birthCountries?.map(country => ({
                value: `${country.regulationCode}-${country.code}`,
                label: country.name
            }));

            this.updateSelectWithOptions('countryOfResidence', options);
            this.toggleFieldVisibility('countryOfResidence', options.length > 0);
        },

        updateProvincesList: function() {
            const provinces = this.model.getProvinces();
            const options = provinces?.map(province => ({
                value: province.abbreviation,
                label: province.name
            }));

            this.updateSelectWithOptions('provinceOfResidence', options);
            this.toggleFieldVisibility('provinceOfResidence', options.length > 0);
        },

        updateCitiesList: function() {
            const selectedProvinceCode = this.$el.find('#provinceOfResidence').val();
            const cities = this.model.getProvinceCities(selectedProvinceCode);
            // officialCode is used to validate italianID field
            // name is sent in CreatePlayerInfoDataMap
            const options = cities?.map(city => ({
                value: `${city.officialCode}-${city.name}`,
                label: city.name
            }));

            this.updateSelectWithOptions('cityOfResidence', options);
            this.toggleFieldVisibility('cityOfResidence', options.length > 0);
        },

        disableField: function(name) {
            const $el = this.$el.find(`#${name}:not(.inner-field-disabled)`);

            $el.attr('disabled', 'disabled').addClass('inner-field-disabled');
        },

        enableField: function(name) {
            const $el = this.$el.find(`#${name}.inner-field-disabled`);

            $el.removeAttr('disabled').removeClass('inner-field-disabled');
        },

        toggleFieldVisibility: function(name, isVisible) {
            const $field = this.$el.find(`.field_name_${name}`);

            if (isVisible) {
                this.enableField(name);
            } else {
                this.disableField(name);
            }

            $field.toggleClass('hidden', !isVisible);
        },

        updateSelectWithOptions: function(name, options = []) {
            const $el = this.$el.find(`#${name}`);

            $el.val('');
            $el.find('option:not([value=""])').remove();
            this.options.form.refreshSelectField($el);

            if (options.length > 0) {
                $el.append(options
                    .map(option => ({
                        value: util.replaceHtmlEntities(option.value),
                        label: util.replaceHtmlEntities(option.label)
                    }))
                    .sort((op1, op2) => {
                          if (op1.label < op2.label) {
                              return -1;
                          }
                          if (op1.label > op2.label) {
                              return 1;
                          }
                          return 0;
                    })
                    .reduce((accHtml, { value, label }) => accHtml + `<option value="${value}">${label}</option>`, '')
                );
            }
        }
    });
});
